import api from '../api/instance';
 
import { Environment, Page } from '@testsigmainc/models';
import { NOOP } from '@testsigma/atoms';
import { useQuery } from 'react-query';
import { PrivateURLConstants } from '@constants/private-url.constant';

const nameSpace = PrivateURLConstants.ENVIRONMENTS;
const variablesNameSpace = PrivateURLConstants.VARIABLES;

type findAllProps = {
  query: string;
  sort?: string;
  size?: string;
  page?: number;
};

type useEnvironmentFindAllProps = {
  payload: findAllProps;
  enabled: boolean;
  callback: (status, response) => void;
  beforeQuery?: () => void;
  key?;
};

const findAll = (
  query?: string,
  sort?: string,
  size?: string,
  page?: number,
) => {
  return api
    .get(nameSpace, { query, sort, size, page })
    .then((res) => new Page<Environment>().deserialize(res, Environment))
    .catch((e) => Promise.reject(e));
};

const show = (environmentId: number) => {
  return api
    .get(nameSpace + '/' + environmentId)
    .then((res) => new Environment().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const remove = (environmentId: number) => {
  return api
    .remove(nameSpace + `/${environmentId}`)
    .then((res) => {
      return res;
    })
    .catch((e) => {
      throw e;
    });
};

const bulkDeleted = (ids: number[]) => {
  return api
    .remove(`${nameSpace}/bulk?${`ids[]`}=${ids.join()}`)
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const update = (environmentId: number, environment: Environment) => {
  return api
    .put(`${nameSpace}/${environmentId}`, environment)
    .then((res) => new Environment().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const create = (environment: Environment) => {
  return api
    .post(nameSpace, environment)
    .then((res) => new Environment().deserialize(res))
    .catch((e) => Promise.reject(e));
};

export const useEnvironmentFindAll = ({
  payload,
  key = null,
  callback,
  beforeQuery = NOOP,
}: useEnvironmentFindAllProps) => {
  return useQuery({
    queryKey: key || payload,
    queryFn: () => {
      beforeQuery();
      return findAll(payload.query, payload.sort, payload.size, payload.page);
    },
    onSuccess: (data) => callback(true, data),
    onError: (err) => callback(false, err),
  });
};

const getVariables = (projectId, page, search = '', size = 25) =>
  api.get(variablesNameSpace, {
    query: `projectId:${projectId}${search ? `,key:*${search}*` : ''}`,
    sort: 'updatedAtEpoch,desc',
    size,
    page,
  });
const getVariable = (projectId, variableId) =>
  api.get(`${variablesNameSpace}/${variableId}`);

const updateVariables = (projectId, payload) =>
  api.put(variablesNameSpace, payload, { projectId });

const dependencyCheckVariable = (variableId) =>
  api.get(`${variablesNameSpace}/${variableId}/dependencies`);

const getEnvironmentVariables = (
  projectId,
  environmentId,
  page,
  key = '',
  size = 25,
) =>
  api.get(
    `${variablesNameSpace}/${projectId}/${environmentId}${
      key ? '/search' : ''
    }`,
    {
      ...(key && { key }),
      sort: 'updatedAtEpoch,desc',
      size,
      page,
    },
  );

const updateEnvVariables = (environmentId, payload) =>
  api.put(`${nameSpace}/${environmentId}`, payload);

const cloneEnvironment = (payload) => api.post(`${nameSpace}/copy`, payload);

const EnvironmentService = {
  findAll,
  show,
  remove,
  bulkDeleted,
  update,
  create,
  updateVariables,
  getVariables,
  getEnvironmentVariables,
  dependencyCheckVariable,
  updateEnvVariables,
  cloneEnvironment,
  getVariable,
};

export default EnvironmentService;
